import { ArrowRight, Check, LucideIcon, Sparkles } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import DemoRequestForm from "./DemoRequestForm";

interface Bullet {
  icon: LucideIcon;
  title: string;
  body: string;
}

interface Props {
  audience: "funders" | "agencies";
  eyebrow: string;
  title: string;
  subtitle: string;
  bullets: Bullet[];
  pricingNote: string;
}

export default function IntelligenceLanding({
  audience,
  eyebrow,
  title,
  subtitle,
  bullets,
  pricingNote,
}: Props) {
  const proofPoints =
    audience === "funders"
      ? [
          "Licensed access to quarterly Alabama civic intelligence reports",
          "Anonymized aggregates API + embeddable widgets",
          "Up to 10 org seats with role-based access",
          "Dedicated onboarding & analyst hours",
        ]
      : [
          "Program-level dashboards for procurement, DBE, and community impact",
          "Custom alerts on bills, funders, and counties you serve",
          "Up to 10 agency seats with SSO-ready accounts",
          "Dedicated onboarding & analyst hours",
        ];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="container max-w-5xl mx-auto px-4 py-12 md:py-20">
        <div className="text-center mb-12">
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary">
            <Sparkles className="h-3 w-3 mr-1" /> {eyebrow}
          </Badge>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
            {title}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {subtitle}
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg">
              <Link to="/pricing">
                See Intelligence pricing <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="#demo">Request a demo</a>
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-12">
          {bullets.map((b) => (
            <div
              key={b.title}
              className="rounded-2xl border border-border bg-card p-6"
            >
              <b.icon className="h-6 w-6 text-primary mb-3" />
              <h3 className="font-display text-lg font-semibold text-foreground mb-1">
                {b.title}
              </h3>
              <p className="text-sm text-muted-foreground">{b.body}</p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 md:p-8">
          <h2 className="font-display text-2xl font-semibold text-foreground mb-4">
            What's included
          </h2>
          <ul className="space-y-2 mb-6">
            {proofPoints.map((p) => (
              <li key={p} className="flex items-start gap-2 text-sm text-foreground/90">
                <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground mb-4">{pricingNote}</p>
          <Button asChild>
            <Link to="/pricing">
              View plans <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>

        <section id="demo" className="mt-12 scroll-mt-24">
          <div className="text-center mb-6">
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground">
              See it on your portfolio
            </h2>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl mx-auto">
              A 30-minute walkthrough with a sample Alabama intelligence briefing tailored to the
              sectors and counties you care about.
            </p>
          </div>
          <DemoRequestForm audience={audience} />
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}